<template>
  <div class="brand-detail-page">


    <!-- 主内容区 -->
    <el-card class="main-card" shadow="hover">
      <div class="card-header">
        <h2>品牌详情</h2>
        <div class="header-actions">
          <el-button
              type="primary"
              @click="handleEditBrand"
              v-if="hasPermission('brand:edit')"
              class="btn-center"
          >
            编辑品牌
          </el-button>
        </div>
      </div>

      <!-- 品牌详情信息 -->
      <div class="brand-info-section" v-loading="loading">
        <h3 class="section-title">品牌基本信息</h3>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-descriptions column="1" border>
              <el-descriptions-item label="品牌名称">{{ brandDetail.brandName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="品牌编码">{{ brandDetail.brandCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="创始人">{{ brandDetail.founder || '-' }}</el-descriptions-item>
              <el-descriptions-item label="创始人联系方式">{{ brandDetail.founderPhone || '-' }}</el-descriptions-item>
            </el-descriptions>
          </el-col>
          <el-col :span="12">
            <el-descriptions column="1" border>
              <el-descriptions-item label="实际地址">{{ brandDetail.entDetailAddress || '-' }}</el-descriptions-item>
              <el-descriptions-item label="首店开业时间">{{ formatDate(brandDetail.firstStoreOpenTime) || '-' }}</el-descriptions-item>
              <el-descriptions-item label="营业执照编码">{{brandDetail.brandBusinessLicense}}</el-descriptions-item>
            </el-descriptions>
          </el-col>
        </el-row>
      </div>

      <!-- 营业额图标 -->
      <div class="turnover-chart-section" v-loading="chartLoading">
        <div class="chart-header">
          <h3 class="section-title">近一年营业额趋势</h3>
          <div class="filter-controls">
            <el-select v-model="selectedStore" placeholder="选择门店" style="width: 200px; margin-right: 10px;">
              <el-option label="所有门店" value="all"></el-option>
              <el-option v-for="store in storeList" :key="store.storeEntCode" :label="store.storeName" :value="store.storeEntCode"></el-option>
            </el-select>
            <el-select v-model="timeGranularity" placeholder="选择时间粒度" style="width: 160px;">
              <el-option label="按日" value="day"></el-option>
              <el-option label="按月" value="month"></el-option>
            </el-select>
          </div>
        </div>
        <div ref="turnoverChart" class="chart-container"></div>
      </div>

      <!-- 门店列表 -->
      <div class="store-list-section" v-loading="storeLoading">
        <div class="store-list-header">
          <h3 class="section-title">品牌门店列表</h3>
          <el-button
              type="success"
              @click="openAddStoreDialog"
              v-if="hasPermission('store:add')"
              class="btn-center"
          >
            新增门店
          </el-button>
        </div>

        <!-- 门店表格 -->
        <el-table
            :data="storeList"
            border
            style="width: 100%"
            @selection-change="handleStoreSelectionChange"
        >
          <el-table-column type="selection" width="55"></el-table-column>
          <el-table-column prop="storeEntCode" label="门店企业编码" min-width="150" align="center"></el-table-column>

          <el-table-column label="门店简称" width="150">
            <template #default="scope">
              <router-link
                  :to="{ name: 'storeDetail', params: { storeEntCode: scope.row.storeEntCode } }"
                  class="store-name-link"
                  v-if="hasPermission('store:detail')"
              >
                {{ scope.row.storeName }}
              </router-link>
            </template>
          </el-table-column>

          <el-table-column prop="storeEntName" label="门店企业名称" min-width="200"></el-table-column>
          <el-table-column prop="storePattern" label="开店模式" min-width="120"></el-table-column>
          <el-table-column prop="province" label="所在省份" min-width="100"></el-table-column>
          <el-table-column prop="city" label="所在城市" min-width="100"></el-table-column>
          <el-table-column prop="entDetailAddress" label="详细地址" min-width="200"></el-table-column>
          <el-table-column prop="storeBusinessLicense" label="统一社会信用代码" min-width="180"></el-table-column>
          <el-table-column prop="groundLeaseTermBegin" label="租期开始" min-width="160" align="center">
            <template #default="scope">{{ formatDate(scope.row.groundLeaseTermBegin) }}</template>
          </el-table-column>
          <el-table-column prop="groundLeaseTermEnd" label="租期结束" min-width="160" align="center">
            <template #default="scope">{{ formatDate(scope.row.groundLeaseTermEnd) }}</template>
          </el-table-column>
          <el-table-column prop="rentTerm" label="租期时长" min-width="100" align="center"></el-table-column>
          <el-table-column prop="investAmountForOpening" label="开店投资金额" min-width="140" align="right">
            <template #default="scope">{{ scope.row.investAmountForOpening ? scope.row.investAmountForOpening.toFixed(2) : '-' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="280" align="center">
            <template #default="scope">
              <el-button
                  type="primary"
                  size="small"
                  @click="handleEditStore(scope.row)"
                  v-if="hasPermission('brand:store')"
                  class="btn-center"
              >编辑</el-button>
              <el-button
                  type="success"
                  size="small"
                  @click="openTurnoverReportDialog(scope.row)"
                  v-if="hasPermission('brand:store:turnover')"
                  class="btn-center"
              >营业额上报</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
              @size-change="handleStoreSizeChange"
              @current-change="handleStoreCurrentChange"
              :current-page="storePagination.currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="storePagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="storePagination.total"
          ></el-pagination>
        </div>
      </div>

      <!-- 联营协议管理 -->
      <div class="store-list-section" v-loading="agreementLoading">
        <div class="store-list-header">
          <h3 class="section-title">品牌联营协议</h3>
          <div>
            <el-button type="primary" @click="openCreateAgreementDialog" class="btn-center">新增品牌级协议</el-button>
            <el-button type="success" @click="openBindStoresDialog" :disabled="!selectedAgreementNo" class="btn-center">为选中协议绑定门店</el-button>
          </div>
        </div>

        <el-table :data="agreementList" border style="width: 100%" @selection-change="handleAgreementSelectionChange">
          <el-table-column type="selection" width="55"></el-table-column>
          <el-table-column prop="agreementNo" label="协议编号" min-width="160"/>
          <el-table-column prop="agreementLevel" label="级别" width="100"/>
          <el-table-column prop="jointOperationType" label="类型" width="120">
            <template #default="scope">{{ scope.row.jointOperationType === '2' ? '固定收益' : '固定本息' }}</template>
          </el-table-column>
          <el-table-column prop="initialShareRatio" label="统一分成(%)" width="140">
            <template #default="scope">{{ scope.row.initialShareRatio ? (Number(scope.row.initialShareRatio) * 100).toFixed(2) : '-' }}</template>
          </el-table-column>
          <el-table-column prop="fixedMonthlyRate" label="固定月息(%)" width="140">
            <template #default="scope">{{ scope.row.fixedMonthlyRate ? Number(scope.row.fixedMonthlyRate).toFixed(2) : '-' }}</template>
          </el-table-column>
          <el-table-column prop="investAmount" label="投资额" width="120"/>
          <el-table-column prop="status" label="状态" width="100"/>
        </el-table>

        <div class="pagination-container">
          <el-pagination
              :current-page="agreementPagination.currentPage"
              :page-sizes="[10,20,50]"
              :page-size="agreementPagination.pageSize"
              layout="total, sizes, prev, pager, next"
              :total="agreementPagination.total"
              @size-change="handleAgreementSizeChange"
              @current-change="handleAgreementCurrentChange"
          />
        </div>
      </div>

      <!-- 门店-打款账号映射管理 -->
      <div class="store-list-section" v-loading="bankMapLoading">
        <div class="store-list-header">
          <h3 class="section-title">门店-手动打款账号映射</h3>
          <el-button type="primary" @click="openAddBankMapDialog" class="btn-center">新增映射</el-button>
        </div>
        <el-table :data="bankMapList" border style="width: 100%">
          <el-table-column prop="storeEntCode" label="门店编码" min-width="160"/>
          <el-table-column prop="payerAccount" label="打款账号" min-width="160"/>
          <el-table-column prop="bankName" label="银行" min-width="160"/>
          <el-table-column prop="effectiveFrom" label="生效开始" min-width="160"/>
          <el-table-column prop="effectiveTo" label="生效结束" min-width="160"/>
          <el-table-column label="状态" width="120">
            <template #default="scope">
              <el-switch v-model="scope.row.status" :active-value="1" :inactive-value="0" @change="val => changeBankMapStatus(scope.row, val)" />
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 出资信息与收益分配（全部协议） -->
      <div class="store-list-section">
        <div class="store-list-header">
          <h3 class="section-title">出资信息（全部协议）</h3>
          <el-button type="primary" @click="openCreateFundDialog" class="btn-center">新增出资信息</el-button>
        </div>

        <el-table :data="funderList" border style="width: 100%">
          <el-table-column prop="agreementNo" label="协议编号" min-width="160"/>
          <el-table-column prop="tenantName" label="出资方" min-width="160"/>
          <el-table-column label="出资金额(元)">
            <template #default="scope">{{ Number(scope.row.fundAmount || 0).toFixed(2) }}</template>
          </el-table-column>
          <el-table-column label="出资比例(%)" width="140">
            <template #default="scope">{{ scope.row.fundRatio || '-' }}</template>
          </el-table-column>
          <el-table-column label="出资时间" width="160">
            <template #default="scope">{{ formatDate(scope.row.fundTime) }}</template>
          </el-table-column>
          <el-table-column prop="fundAccount" label="出资账户" />
          <el-table-column prop="receiptAccount" label="到账账户" />
          <el-table-column label="银行回单" width="140">
            <template #default="scope">
              <el-link v-if="scope.row.bankReceiptUrl" type="primary" :href="scope.row.bankReceiptUrl" target="_blank">查看回单</el-link>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>

        <div class="store-list-header" style="margin-top: 20px;">
          <h3 class="section-title">收益分配方案（全部协议）</h3>
          <el-button type="primary" @click="openCreateProfitDialog" class="btn-center">新增收益分配</el-button>
        </div>

        <el-table :data="profitList" border style="width: 100%">
          <el-table-column prop="agreementNo" label="协议编号" width="160"/>
          <el-table-column prop="tenant" label="收益分配对象" width="160"/>
          <el-table-column prop="fundSerialId" label="出资流水号" width="140"/>
          <el-table-column prop="profitSharingPlan" label="收益分享方案" width="140"/>
          <el-table-column label="优先层保底收益(%)" width="160">
            <template #default="scope">{{ scope.row.seniorFloorFloorIncome || '-' }}</template>
          </el-table-column>
          <el-table-column label="额外收益分成比例(%)" width="160">
            <template #default="scope">{{ scope.row.additionalProfitShareRatio || '-' }}</template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>

    <!-- 新增/编辑门店弹窗 -->
    <el-dialog
        :title="storeDialogTitle"
        v-model="storeDialogVisible"
        width="800px"
        :close-on-click-modal="false"
        :fullscreen="false"
    >
      <!-- 切换添加方式的标签页 -->
      <el-tabs v-model="activeTab" type="card">
        <el-tab-pane label="手动添加" name="manual">
          <el-form
              ref="storeFormRef"
              :model="storeFormData"
              :rules="storeFormRules"
              label-width="150px"
              class="store-form"
          >
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="门店企业编码" prop="storeEntCode" v-if="!(storeFormData.storeEntCode === '')">
                  <el-input
                      v-model="storeFormData.storeEntCode"
                      placeholder="请输入门店企业编码"
                      maxlength="100"
                  ></el-input>
                </el-form-item>
                <el-form-item label="门店简称" prop="storeName">
                  <el-input
                      v-model="storeFormData.storeName"
                      placeholder="请输入门店简称"
                      maxlength="100"
                  ></el-input>
                </el-form-item>
                <el-form-item label="开店模式" prop="storePattern">
                  <el-input
                      v-model="storeFormData.storePattern"
                      placeholder="请输入开店模式"
                      maxlength="100"
                  ></el-input>
                </el-form-item>
                <el-form-item label="所在省份" prop="province">
                  <el-input
                      v-model="storeFormData.province"
                      placeholder="请输入所在省份"
                      maxlength="255"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="所在城市" prop="city">
                  <el-input
                      v-model="storeFormData.city"
                      placeholder="请输入所在城市"
                      maxlength="255"
                  ></el-input>
                </el-form-item>
                <el-form-item label="门店统一社会信用代码" prop="storeBusinessLicense">
                  <el-input
                      v-model="storeFormData.storeBusinessLicense"
                      placeholder="请输入统一社会信用代码"
                      maxlength="100"
                  ></el-input>
                </el-form-item>
                <el-form-item label="门店企业名称" prop="storeEntName">
                  <el-input
                      v-model="storeFormData.storeEntName"
                      placeholder="请输入门店企业名称"
                      maxlength="100"
                  ></el-input>
                </el-form-item>
                <el-form-item label="场地租期开始时间" prop="groundLeaseTermBegin">
                  <el-date-picker
                      v-model="storeFormData.groundLeaseTermBegin"
                      type="date"
                      placeholder="选择开始时间"
                      value-format="YYYY-MM-DD"
                  ></el-date-picker>
                </el-form-item>
                <el-form-item label="场地租期结束时间" prop="groundLeaseTermEnd">
                  <el-date-picker
                      v-model="storeFormData.groundLeaseTermEnd"
                      type="date"
                      placeholder="选择结束时间"
                      value-format="YYYY-MM-DD"
                  ></el-date-picker>
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item label="详细地址" prop="entDetailAddress">
              <el-input
                  v-model="storeFormData.entDetailAddress"
                  placeholder="请输入详细地址"
                  maxlength="100"
              ></el-input>
            </el-form-item>
            <el-form-item label="开店投资金额" prop="investAmountForOpening">
              <el-input
                  v-model.number="storeFormData.investAmountForOpening"
                  placeholder="请输入开店投资金额"
                  type="number"
              ></el-input>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="批量导入" name="batch">
          <div class="batch-import-section">
            <div class="import-guide">
              <p>1. 下载批量导入模板</p>
              <el-button
                  type="primary"
                  icon="Download"
                  @click="downloadTemplate"
                  style="margin-bottom: 20px"
                  class="btn-center"
              >
                下载模板
              </el-button>

              <p>2. 按照模板填写门店信息</p>
              <p>3. 上传Excel文件</p>
              <el-upload
                  class="upload-excel"
                  action=""
                  :on-change="handleFileChange"
                  :before-upload="beforeUpload"
                  :auto-upload="false"
                  accept=".xlsx,.xls"
              >
                <el-button type="primary" icon="Upload" class="btn-center">选择Excel文件</el-button>
                <div  class="el-upload__tip">
                  支持上传 .xlsx, .xls 格式文件，不超过5MB
                </div>
              </el-upload>

              <div v-if="uploadedFileName" class="uploaded-file-info">
                <i class="el-icon-success"></i>
                <span>已选择文件: {{ uploadedFileName }}</span>
                <el-button
                    type="text"
                    icon="Close"
                    @click="clearUploadedFile"
                    class="remove-file-btn btn-center"
                ></el-button>
              </div>
            </div>

            <!-- 解析结果预览 -->
            <div v-if="previewStoreList.length > 0" class="preview-section">
              <h4>数据预览 (共 {{ previewStoreList.length }} 条)</h4>
              <el-table
                  :data="previewStoreList"
                  border
                  size="mini"
                  style="width: 100%; margin-top: 10px"
                  max-height="300"
              >
                <el-table-column prop="storeEntCode" label="门店企业编码" width="150"></el-table-column>
                <el-table-column prop="storeName" label="门店简称" width="120"></el-table-column>
                <el-table-column prop="storeEntName" label="门店企业名称" width="180"></el-table-column>
                <el-table-column prop="errorMsg" label="校验结果" width="200">
                  <template #default="scope">
                    <span v-if="scope.row.errorMsg" class="error-text">{{ scope.row.errorMsg }}</span>
                    <span v-else class="success-text">校验通过</span>
                  </template>
                </el-table-column>
              </el-table>

              <div v-if="hasInvalidData" class="validation-warning">
                <i class="el-icon-warning"></i>
                <span>部分数据校验不通过，请检查并修正后重新上传</span>
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <el-button @click="closeStoreDialog" class="btn-center">取消</el-button>
        <el-button
            type="primary"
            @click="submitStoreForm"
            :loading="submittingStore"
            :disabled="activeTab === 'batch' && (previewStoreList.length === 0 || hasInvalidData)"
            class="btn-center"
        >
          确认
        </el-button>
      </template>
    </el-dialog>

  </div>

   <el-dialog v-model="createAgreementDialogVisible" title="新增品牌级联营协议" width="600px">
      <el-form :model="createAgreementForm" :rules="createAgreementRules" ref="createAgreementFormRef" label-width="140px">
        <el-form-item label="协议编号" prop="agreementNo">
          <el-input v-model="createAgreementForm.agreementNo" placeholder="请输入协议编号"></el-input>
        </el-form-item>
        <el-form-item label="品牌编码">
          <el-input v-model="createAgreementForm.brandCode" disabled></el-input>
        </el-form-item>
        <el-form-item label="联营开始时间" prop="jointStartTime">
          <el-date-picker v-model="createAgreementForm.jointStartTime" type="date" value-format="YYYY-MM-DD" placeholder="选择开始时间" />
        </el-form-item>
        <el-form-item label="联营结束时间">
          <el-date-picker v-model="createAgreementForm.jointEndTime" type="date" value-format="YYYY-MM-DD" placeholder="选择结束时间(可选)" />
        </el-form-item>
        <el-form-item label="联合经营类型">
          <el-input v-model="createAgreementForm.jointOperationType" disabled></el-input>
        </el-form-item>
        <el-form-item label="协议等级">
          <el-input v-model="createAgreementForm.agreementLevel" disabled></el-input>
        </el-form-item>
        <el-form-item label="统一分成(小数)">
          <el-input v-model.number="createAgreementForm.initialShareRatio" placeholder="如 0.15"></el-input>
        </el-form-item>
        <el-form-item label="固定月息(百分比)">
          <el-input v-model.number="createAgreementForm.fixedMonthlyRate" placeholder="如 1.2"></el-input>
        </el-form-item>
        <el-form-item label="投资额">
          <el-input v-model.number="createAgreementForm.investAmount" type="number"></el-input>
        </el-form-item>
        <el-form-item label="联营状态">
          <el-input v-model="createAgreementForm.status" disabled></el-input>
        </el-form-item>
        <el-form-item label="关联门店">
          <div style="margin-bottom: 10px; max-height: 150px; overflow-y: auto; border: 1px solid #dcdfe6; border-radius: 4px; padding: 8px;">
            <el-checkbox
              v-for="s in storeList"
              :key="s.storeEntCode"
              :label="s.storeEntCode"
              v-model="s.selected"
              @change="onCreateStoreCheckChange"
              style="display: block; padding: 4px 0;"
            >{{ s.storeName }}</el-checkbox>
          </div>
          <el-table :data="createAgreementForm.stores" border size="small" max-height="200" v-if="createAgreementForm.stores.length > 0">
            <el-table-column prop="storeName" label="门店名称" min-width="150" />
            <el-table-column label="分成比例(%)" width="180">
              <template #default="scope">
                <el-input-number
                  v-model="scope.row.storeShareRatio"
                  :min="0" :max="100" :precision="2" :step="0.1"
                  size="small"
                  style="width: 100%;"
                />
              </template>
            </el-table-column>
          </el-table>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createAgreementDialogVisible=false">取消</el-button>
        <el-button type="primary" @click="submitCreateAgreement">提交</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="bindStoresDialogVisible" title="为协议绑定门店" width="600px">
      <div style="margin-bottom: 10px; max-height: 150px; overflow-y: auto; border: 1px solid #dcdfe6; border-radius: 4px; padding: 8px;">
        <el-checkbox
          v-for="s in storeList"
          :key="s.storeEntCode"
          :label="s.storeEntCode"
          v-model="s.selected"
          @change="onBindStoreCheckChange"
          style="display: block; padding: 4px 0;"
        >{{ s.storeName }}</el-checkbox>
      </div>
      <el-table :data="bindStoresData" border size="small" max-height="200" v-if="bindStoresData.length > 0">
        <el-table-column prop="storeName" label="门店名称" min-width="150" />
        <el-table-column label="分成比例(%)" width="180">
          <template #default="scope">
            <el-input-number
              v-model="scope.row.storeShareRatio"
              :min="0" :max="100" :precision="2" :step="0.1"
              size="small"
              style="width: 100%;"
            />
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="bindStoresDialogVisible=false">取消</el-button>
        <el-button type="primary" @click="submitBindStores">提交</el-button>
      </template>
    </el-dialog>

      <el-dialog v-model="createFundDialogVisible" title="新增出资信息" width="600px">
        <el-form :model="createFundForm" :rules="fundFormRules" ref="fundFormRef" label-width="140px">
          <el-form-item label="协议编号" prop="agreementNo">
            <el-select v-model="createFundForm.agreementNo" placeholder="请选择协议" @change="onFundAgreementChange">
              <el-option v-for="a in agreementList" :key="a.agreementNo" :label="a.agreementNo" :value="a.agreementNo" />
            </el-select>
          </el-form-item>
        <el-form-item label="出资方" prop="tenantId">
          <el-select v-model="createFundForm.tenantId" placeholder="请选择系统租户">
            <el-option v-for="t in tenants" :key="t.id" :label="t.tenantName" :value="String(t.id)" />
          </el-select>
        </el-form-item>
        <el-form-item label="出资金额(元)" prop="fundAmount">
          <el-input v-model.number="createFundForm.fundAmount" type="number"></el-input>
        </el-form-item>
        <el-form-item label="出资比例(%)" prop="fundRatio">
          <el-input v-model.number="createFundForm.fundRatio" type="number"></el-input>
        </el-form-item>
        <el-form-item label="出资时间" prop="fundTime">
          <el-date-picker v-model="createFundForm.fundTime" type="date" value-format="YYYY-MM-DD"></el-date-picker>
        </el-form-item>
        <el-form-item label="出资账户" prop="fundAccount">
          <el-input v-model="createFundForm.fundAccount"></el-input>
        </el-form-item>
        <el-form-item label="到账账户" prop="receiptAccount">
          <el-input v-model="createFundForm.receiptAccount"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createFundDialogVisible=false">取消</el-button>
        <el-button type="primary" @click="submitCreateFund">提交</el-button>
      </template>
    </el-dialog>

      <el-dialog v-model="createProfitDialogVisible" title="新增收益分配" width="600px">
        <el-form :model="createProfitForm" :rules="{
          agreementNo: [ { required: true, message: '请选择协议', trigger: 'change' } ],
          tenant: [ { required: true, message: '请选择收益分配对象', trigger: 'change' } ],
          fundSerialId: [ { required: true, message: '请选择出资流水号', trigger: 'change' } ],
          profitSharingPlan: [ { required: true, message: '请选择收益方案', trigger: 'change' } ],
          seniorFloorFloorIncome: [ { required: true, message: '请输入保底收益', trigger: 'blur' } ],
          additionalProfitShareRatio: [ { required: true, message: '请输入额外收益分成比例', trigger: 'blur' } ],
        }" ref="profitFormRef" label-width="140px">
          <el-form-item label="协议编号" prop="agreementNo">
            <el-select v-model="createProfitForm.agreementNo" placeholder="请选择协议" @change="onProfitAgreementChange">
              <el-option v-for="a in agreementList" :key="a.agreementNo" :label="a.agreementNo" :value="a.agreementNo" />
            </el-select>
          </el-form-item>
        <el-form-item label="收益分配对象" prop="tenant">
          <el-select v-model="createProfitForm.tenant" placeholder="请选择出资方" @change="onProfitTenantChange">
            <el-option v-for="t in profitTenantOptions" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="出资流水号" prop="fundSerialId">
          <el-select v-model="createProfitForm.fundSerialId" placeholder="请选择出资流水号">
            <el-option v-for="f in profitFundOptions" :key="f.value" :label="f.label" :value="f.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="收益方案" prop="profitSharingPlan">
          <el-select v-model="createProfitForm.profitSharingPlan" placeholder="请选择收益方案">
            <el-option v-for="p in allowedProfitPlans" :key="p" :label="p" :value="p" />
          </el-select>
        </el-form-item>
        <el-form-item label="保底收益(%)" prop="seniorFloorFloorIncome">
          <el-input v-model.number="createProfitForm.seniorFloorFloorIncome" type="number"></el-input>
        </el-form-item>
        <el-form-item label="额外收益分成(%)" prop="additionalProfitShareRatio">
          <el-input v-model.number="createProfitForm.additionalProfitShareRatio" type="number"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createProfitDialogVisible=false">取消</el-button>
        <el-button type="primary" @click="submitCreateProfit">提交</el-button>
      </template>
    </el-dialog>

  <el-dialog v-model="addBankMapDialogVisible" title="新增门店-打款账号映射" width="600px">
    <el-form :model="addBankMapForm" :rules="addBankMapRules" ref="addBankMapFormRef" label-width="140px">
      <el-form-item label="打款账号" prop="payerAccount">
        <el-input v-model="addBankMapForm.payerAccount" placeholder="请输入客户打款账号"></el-input>
      </el-form-item>
      <el-form-item label="开户行">
        <el-input v-model="addBankMapForm.bankName" placeholder="请输入银行名称"></el-input>
      </el-form-item>
      <el-form-item label="生效开始">
        <el-date-picker v-model="addBankMapForm.effectiveFrom" type="date" value-format="YYYY-MM-DD" />
      </el-form-item>
      <el-form-item label="生效结束">
        <el-date-picker v-model="addBankMapForm.effectiveTo" type="date" value-format="YYYY-MM-DD" />
      </el-form-item>
      <el-form-item label="关联门店" prop="storeEntCodes">
        <el-select v-model="addBankMapForm.storeEntCodes" multiple placeholder="选择门店">
          <el-option v-for="s in storeList" :key="s.storeEntCode" :label="s.storeName" :value="s.storeEntCode" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-switch v-model="addBankMapForm.status" :active-value="1" :inactive-value="0" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="addBankMapDialogVisible=false">取消</el-button>
      <el-button type="primary" @click="submitAddBankMap">提交</el-button>
    </template>
  </el-dialog>

  <!-- 营业额上报弹窗 -->
  <el-dialog v-model="turnoverReportDialogVisible" title="门店营业额上报" width="600px">
    <el-form :model="turnoverReportForm" :rules="turnoverReportRules" ref="turnoverReportFormRef" label-width="140px">
      <el-form-item label="门店名称">
        <el-input v-model="turnoverReportForm.storeName" disabled></el-input>
      </el-form-item>
      <el-form-item label="门店编码">
        <el-input v-model="turnoverReportForm.storeEntCode" disabled></el-input>
      </el-form-item>
      <el-form-item label="经营日期" prop="incomeDate">
        <el-date-picker v-model="turnoverReportForm.incomeDate" type="date" value-format="YYYY-MM-DD" placeholder="选择经营日期" />
      </el-form-item>
      <el-form-item label="营业额" prop="turnover">
        <el-input v-model.number="turnoverReportForm.turnover" type="number" placeholder="请输入营业额"></el-input>
      </el-form-item>
      <el-form-item label="实际到账金额" prop="actualAmount">
        <el-input v-model.number="turnoverReportForm.actualAmount" type="number" placeholder="请输入实际到账金额"></el-input>
      </el-form-item>
      <el-form-item label="收入渠道" prop="dimensionKey">
        <el-input v-model="turnoverReportForm.dimensionKey" placeholder="收入渠道"></el-input>
      </el-form-item>
      <el-form-item label="状态" prop="status">
          <el-select v-model="turnoverReportForm.status" placeholder="请选择状态">
            <el-option label="分成完成" value="1"></el-option>
            <el-option label="部分完成" value="2"></el-option>
            <el-option label="数据失效" value="3"></el-option>
            <el-option label="未分成" value="4"></el-option>
          </el-select>
        </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="turnoverReportDialogVisible=false">取消</el-button>
      <el-button type="primary" @click="submitTurnoverReport" :loading="submittingTurnover">提交</el-button>
    </template>
  </el-dialog>

</template>

<script>
import {ref, reactive, onMounted, nextTick, computed, watch} from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import * as XLSX from 'xlsx'
import store from "@/store";
import assetService from "@/services/assetService";
import router from "@/router";
import * as echarts from 'echarts'
import tenantService from "@/services/tenantService";

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN')
}

// 验证门店数据
const validateStoreData = (store) => {
  const errors = []

  if (!store.storeName) {
    errors.push('门店简称不能为空')
  } else if (store.storeName.length > 100) {
    errors.push('门店简称不能超过100个字符')
  }

  if (!store.storeEntName) {
    errors.push('门店企业名称不能为空')
  } else if (store.storeEntName.length > 100) {
    errors.push('门店企业名称不能超过100个字符')
  }

  if (!store.storeBusinessLicense) {
    errors.push('统一社会信用代码不能为空')
  } else if (store.storeBusinessLicense.length > 100) {
    errors.push('统一社会信用代码不能超过100个字符')
  }

  if (!store.province) {
    errors.push('所在省份不能为空')
  }

  if (!store.city) {
    errors.push('所在城市不能为空')
  }

  if (!store.entDetailAddress) {
    errors.push('详细地址不能为空')
  } else if (store.entDetailAddress.length > 100) {
    errors.push('详细地址不能超过100个字符')
  }

  if (!store.groundLeaseTermBegin) {
    errors.push('场地租期开始时间不能为空')
  }

  if (!store.groundLeaseTermEnd) {
    errors.push('场地租期结束时间不能为空')
  } else if (new Date(store.groundLeaseTermEnd) <= new Date(store.groundLeaseTermBegin)) {
    console.error(new Date(store.groundLeaseTermEnd),new Date(store.groundLeaseTermBegin))
    errors.push('租期结束时间必须晚于开始时间')
  }

  // 修改投资金额验证逻辑，允许0或正数
  if (store.investAmountForOpening === null || store.investAmountForOpening === undefined) {
    errors.push('开店投资金额不能为空')
  } else if (store.investAmountForOpening < 0) {
    errors.push('开店投资金额不能为负数')
  }

  return errors.length > 0 ? errors.join('；') : null
}

export default {
  name: 'BrandDetail',
  setup() {
    // 获取路由和路由参数
    const route = useRoute()
    const brandCode = route.params.brandCode


    // 品牌详情数据 - 驼峰命名
    const brandDetail = reactive({
      brandName: '',
      brandCode: '',
      brandBusinessLicense: '',
      founder: '',
      founderPhone: '',
      entDetailAddress: '',
      firstStoreOpenTime: ''
    })

    // 门店列表数据 - 驼峰命名
    const storeList = ref([])
    const storePagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0
    })

    // 加载状态
    const loading = ref(true)
    const storeLoading = ref(false)
    const agreementLoading = ref(false)
    const submittingStore = ref(false)
    const chartLoading = ref(false)

    // 门店弹窗状态
    const storeDialogVisible = ref(false)
    const storeDialogTitle = ref('新增门店')
    const activeTab = ref('manual') // 手动添加/批量导入
    const selectedStore = ref('all')
    const timeGranularity = ref('month')
    const turnoverChart = ref(null)

    // 协议列表与选择
    const agreementList = ref([])
    const agreementPagination = reactive({ currentPage: 1, pageSize: 10, total: 0 })
    const selectedAgreementNo = ref('')

    // 出资与收益分配
    const funderList = ref([])
    const profitList = ref([])
    const fundCandidates = ref([])
    const tenants = ref([])
    const profitTenantOptions = ref([])
    const profitFundOptions = ref([])
    const allowedProfitPlans = ref([])
    const currentAgreementFunds = ref([])

    // 门店表单数据 - 驼峰命名
    const storeFormData = reactive({
      storeEntCode: '',
      storeName: '',
      storePattern: '',
      storeEntName: '',
      province: '',
      city: '',
      storeBusinessLicense: '',
      groundLeaseTermBegin: '',
      groundLeaseTermEnd: '',
      rentTerm: null,
      entDetailAddress: '',
      brandCode: brandCode,
      investAmountForOpening: null
    })

    // 批量导入相关
    const uploadedFileName = ref('')
    const uploadedFile = ref(null)
    const previewStoreList = ref([])
    const hasInvalidData = ref(false)

    // 营业额上报相关
    const turnoverReportDialogVisible = ref(false)
    const submittingTurnover = ref(false)
    const turnoverReportForm = reactive({
      storeName: '',
      storeEntCode: '',
      incomeDate: '',
      turnover: null,
      actualAmount: null,
      dimensionKey: '全量收入',
      status: '4'
    })
    const turnoverReportRules = reactive({
      incomeDate: [
        { required: true, message: '请选择经营日期', trigger: 'change' }
      ],
      turnover: [
        { required: true, message: '请输入营业额', trigger: 'blur' },
        { type: 'number', message: '请输入有效的数字', trigger: 'blur' },
        { validator: (rule, value, callback) => {
            if (value < 0) {
              callback(new Error('营业额不能为负数'))
            } else {
              callback()
            }
          }, trigger: 'blur' }
      ],
      actualAmount: [
        { required: true, message: '请输入实际到账金额', trigger: 'blur' },
        { type: 'number', message: '请输入有效的数字', trigger: 'blur' },
        { validator: (rule, value, callback) => {
            if (value < 0) {
              callback(new Error('实际到账金额不能为负数'))
            } else {
              callback()
            }
          }, trigger: 'blur' }
      ],
      dimensionKey: [
        { required: true, message: '请输入维度键', trigger: 'blur' }
      ],
      status: [
        { required: true, message: '请选择状态', trigger: 'change' }
      ]
    })
    const turnoverReportFormRef = ref(null)

    // 营业执照查看弹窗
    const licenseDialogVisible = ref(false)
    const currentLicenseUrl = ref('')

    // 查看门店详情（通过按钮点击）
    const handleView = (storeEntCode) => {
      router.push({
        name: 'storeDetail',
        params: { store_ent_code: storeEntCode }
      })
    }

    // 表单引用
    const storeFormRef = ref(null)

    // 门店表单验证规则 - 驼峰命名
    const storeFormRules = reactive({
      storeEntCode: [
        { required: true, message: '请输入门店企业编码', trigger: 'blur' },
        { max: 100, message: '门店企业编码不能超过100个字符', trigger: 'blur' }
      ],
      storeName: [
        { required: true, message: '请输入门店简称', trigger: 'blur' },
        { max: 100, message: '门店简称不能超过100个字符', trigger: 'blur' }
      ],
      storeBusinessLicense: [
        { required: true, message: '请输入统一社会信用代码', trigger: 'blur' },
        { max: 100, message: '统一社会信用代码不能超过100个字符', trigger: 'blur' }
      ],
      province: [
        { required: true, message: '请输入所在省份', trigger: 'blur' }
      ],
      city: [
        { required: true, message: '请输入所在城市', trigger: 'blur' }
      ],
      entDetailAddress: [
        { required: true, message: '请输入详细地址', trigger: 'blur' },
        { max: 100, message: '详细地址不能超过100个字符', trigger: 'blur' }
      ],
      groundLeaseTermBegin: [
        { required: true, message: '请选择场地租期开始时间', trigger: 'change' }
      ],
      groundLeaseTermEnd: [
        { required: true, message: '请选择场地租期结束时间', trigger: 'change' },
        {
          validator: (rule, value, callback) => {
            if (value && storeFormData.groundLeaseTermBegin) {
              if (new Date(value) <= new Date(storeFormData.groundLeaseTermBegin)) {
                callback(new Error('租期结束时间必须晚于开始时间'))
                // callback()
              } else {
                callback()
              }
            } else {
              callback()
            }
          },
          trigger: 'change'
        }
      ],
      // 修改投资金额验证规则
      investAmountForOpening: [
        { required: true, message: '请输入开店投资金额', trigger: 'blur' },
        { type: 'number', message: '请输入有效的数字', trigger: 'blur' },
        { validator: (rule, value, callback) => {
            if (value < 0) {
              callback(new Error('开店投资金额不能为负数'))
            } else {
              callback()
            }
          }, trigger: 'blur' }
      ]
    })

    // 获取用户权限集合（仅包含权限编码）
    const permissionCodes = computed(() => {
      const permissions = store.state.permissions
      if (!permissions) return []
      // 将权限对象转换为权限编码数组
      return Object.values(permissions).map(perm => perm.permissionCode)
    })

    // 权限判断方法
    const hasPermission = (code) => {
      return permissionCodes.value.includes(code)
    }

    // 打开营业额上报弹窗
    const openTurnoverReportDialog = (store) => {
      // 重置表单
      Object.assign(turnoverReportForm, {
        storeName: store.storeName,
        storeEntCode: store.storeEntCode,
        incomeDate: '',
        turnover: null,
        actualAmount: null,
        dimensionKey: '全量收入',
        status: '4'
      })
      turnoverReportDialogVisible.value = true
    }

    // 提交营业额上报
    const submitTurnoverReport = async () => {
      try {
        if (!turnoverReportFormRef.value) return
        await turnoverReportFormRef.value.validate()
        submittingTurnover.value = true

        const reportData = {
          storeEntCode: turnoverReportForm.storeEntCode,
          incomeDate: turnoverReportForm.incomeDate,
          dimensionKey: turnoverReportForm.dimensionKey,
          turnover: turnoverReportForm.turnover,
          actualAmount: turnoverReportForm.actualAmount,
          status: turnoverReportForm.status
        }

        await assetService.reportStoreIncome(reportData)
        ElMessage.success('营业额上报成功')
        turnoverReportDialogVisible.value = false
      } catch (error) {
        ElMessage.error('营业额上报失败：' + (error.message || ''))
      } finally {
        submittingTurnover.value = false
      }
    }

    // 获取品牌详情
    const getBrandDetail = async () => {
      try {
        loading.value = true
        const response = await assetService.getBrandBaseInfo({brandCode: brandCode})
        const data = response.data
        // 填充详情数据，驼峰命名匹配
        Object.keys(brandDetail).forEach(key => {
          brandDetail[key] = data[key] !== undefined ? data[key] : ''
        })
      } catch (error) {
        ElMessage.error('获取品牌详情失败：' + (error.message || ''))
      } finally {
        loading.value = false
      }
    }

    // 获取门店列表
    const getStoreList = async () => {
      try {
        storeLoading.value = true
        const params = {
          brandCode: brandCode,
          pageNum: storePagination.currentPage,
          pageSize: storePagination.pageSize
        }

        const response = await assetService.getStoreListByBrandCode(params)
        storeList.value = (response.data || []).map(s => ({ ...s, storeShareRatio: null, selected: false }))
        storePagination.total = response.data.length
      } catch (error) {
        ElMessage.error('获取门店列表失败：' + (error.message || ''))
      } finally {
        storeLoading.value = false
      }
    }

    // 获取品牌下协议列表
    const getAgreementList = async () => {
      try {
        agreementLoading.value = true
        const res = await assetService.getAgreementListByBrandCode({ brandCode })
        agreementList.value = res.data || []
        agreementPagination.total = agreementList.value.length
        await fetchAllFunderList()
        await fetchAllProfitList()
      } catch (e) {
        ElMessage.error('获取联营协议失败：' + (e.message || ''))
      } finally {
        agreementLoading.value = false
      }
    }

    const fetchBankMappings = async () => {
      try {
        bankMapLoading.value = true
        const res = await assetService.getBankMappingsByBrandCode({ brandCode })
        bankMapList.value = res.data || []
      } catch (e) {
        ElMessage.error('获取账号映射失败：' + (e.message || ''))
      } finally {
        bankMapLoading.value = false
      }
    }

    const fetchAllFunderList = async () => {
      try {
        const tasks = (agreementList.value || []).map(a => assetService.getJointOperationFunds({ agreementNo: a.agreementNo }))
        const results = await Promise.all(tasks)
        const merged = []
        for (const r of results) {
          const list = r.data || []
          merged.push(...list)
        }
        funderList.value = merged
      } catch (e) {
        ElMessage.error('加载全部出资信息失败：' + (e.message || ''))
      }
    }

    const fetchAllProfitList = async () => {
      try {
        const tasks = (agreementList.value || []).map(a => assetService.getProfitByAgreementNo({ agreementNo: a.agreementNo }))
        const results = await Promise.all(tasks)
        const merged = []
        for (const r of results) {
          const list = r.data || []
          merged.push(...list)
        }
        profitList.value = merged
      } catch (e) {
        ElMessage.error('加载全部收益分配失败：' + (e.message || ''))
      }
    }

    const handleAgreementSelectionChange = (rows) => {
      selectedAgreementNo.value = rows && rows[0] ? rows[0].agreementNo : ''
      if (selectedAgreementNo.value) {
        fetchFunderList()
        fetchProfitList()
      } else {
        funderList.value = []
        profitList.value = []
      }
    }

    const handleAgreementSizeChange = (size) => {
      agreementPagination.pageSize = size
    }
    const handleAgreementCurrentChange = (page) => {
      agreementPagination.currentPage = page
    }

    const fetchFunderList = async () => {
      try {
        const res = await assetService.getJointOperationFunds({ agreementNo: selectedAgreementNo.value })
        funderList.value = res.data || []
      } catch (e) {
        ElMessage.error('获取出资信息失败：' + (e.message || ''))
      }
    }

    const fetchProfitList = async () => {
      try {
        const res = await assetService.getProfitByAgreementNo({ agreementNo: selectedAgreementNo.value })
        profitList.value = res.data || []
      } catch (e) {
        ElMessage.error('获取收益分配失败：' + (e.message || ''))
      }
    }

    // 新增品牌级协议弹窗与提交
    const createAgreementDialogVisible = ref(false)
    const createAgreementFormRef = ref(null)
    const selectedStoresForAgreement = ref([])
    const createAgreementForm = reactive({
      agreementNo: '',
      agreementLevel: 'brand',
      jointOperationType: '2',
      brandCode,
      jointStartTime: '',
      jointEndTime: '',
      initialShareRatio: null,
      fixedMonthlyRate: null,
      investAmount: null,
      status: '1',
      stores: []
    })
    const createAgreementRules = reactive({
      agreementNo: [{ required: true, message: '请输入协议编号', trigger: 'blur' }],
      jointStartTime: [{ required: true, message: '请选择联营开始时间', trigger: 'change' }],
      initialShareRatio: [{ required: true, message: '请输入统一分成(小数，如0.15)', trigger: 'blur' }],
      fixedMonthlyRate: [{ required: true, message: '请输入固定月息(百分比，如1.2)', trigger: 'blur' }],
      investAmount: [{ required: true, message: '请输入投资额', trigger: 'blur' }],
      stores: [{ required: true, message: '请选择关联门店', trigger: 'change' }]
    })
    const syncCreateStores = () => {
      const selectedStores = storeList.value.filter(s => s.selected)
      const existingMap = new Map(createAgreementForm.stores.map(s => [s.storeEntCode, s]))
      createAgreementForm.stores = selectedStores.map(s => {
        const existing = existingMap.get(s.storeEntCode)
        if (existing) return existing
        return {
          storeEntCode: s.storeEntCode,
          storeName: s.storeName,
          storeShareRatio: createAgreementForm.initialShareRatio ? createAgreementForm.initialShareRatio * 100 : null
        }
      })
    }
    const onCreateStoreCheckChange = () => {
      syncCreateStores()
    }
    const openCreateAgreementDialog = () => {
      storeList.value.forEach(s => { s.selected = false })
      createAgreementForm.stores = []
      createAgreementDialogVisible.value = true
    }
    const submitCreateAgreement = async () => {
      try {
        await createAgreementFormRef.value.validate()
        const payload = {
          agreement: {
            agreementNo: createAgreementForm.agreementNo,
            agreementLevel: createAgreementForm.agreementLevel,
            jointOperationType: createAgreementForm.jointOperationType,
            brandCode: createAgreementForm.brandCode,
            jointStartTime: createAgreementForm.jointStartTime,
            jointEndTime: createAgreementForm.jointEndTime || null,
            initialShareRatio: createAgreementForm.initialShareRatio,
            fixedMonthlyRate: createAgreementForm.fixedMonthlyRate,
            investAmount: createAgreementForm.investAmount,
            status: createAgreementForm.status
          },
          stores: createAgreementForm.stores.map(s => ({
            storeEntCode: s.storeEntCode,
            storeShareRatio: s.storeShareRatio ? s.storeShareRatio / 100 : null
          }))
        }
        const res = await assetService.createBrandAgreement(payload)
        if (res.code === 200) {
          ElMessage.success('品牌级协议创建成功，已绑定门店数：' + res.data)
          createAgreementDialogVisible.value = false
          await getAgreementList()
        } else {
          ElMessage.error(res.message || '创建协议失败')
        }
      } catch (e) {
        if (e && e.message) ElMessage.error(e.message)
      }
    }

    // 绑定门店弹窗
    const bindStoresDialogVisible = ref(false)
    const bindStoresSelection = ref([])
    const bindStoresData = ref([])
    const syncBindStores = () => {
      const selectedStores = storeList.value.filter(s => s.selected)
      const existingMap = new Map(bindStoresData.value.map(s => [s.storeEntCode, s]))
      bindStoresData.value = selectedStores.map(s => {
        const existing = existingMap.get(s.storeEntCode)
        if (existing) return existing
        return {
          storeEntCode: s.storeEntCode,
          storeName: s.storeName,
          storeShareRatio: null
        }
      })
    }
    const onBindStoreCheckChange = () => {
      syncBindStores()
    }
    const openBindStoresDialog = () => {
      storeList.value.forEach(s => { s.selected = false })
      bindStoresData.value = []
      bindStoresDialogVisible.value = true
    }
    const submitBindStores = async () => {
      try {
        const res = await assetService.bindAgreementStores({
          agreementNo: selectedAgreementNo.value,
          stores: bindStoresData.value.map(s => ({
            storeEntCode: s.storeEntCode,
            storeShareRatio: s.storeShareRatio ? s.storeShareRatio / 100 : null
          }))
        })
        if (res.code === 200) {
          ElMessage.success('已绑定门店数：' + res.data)
          bindStoresDialogVisible.value = false
        } else {
          ElMessage.error(res.message || '绑定门店失败')
        }
      } catch (e) {
        ElMessage.error(e.message || '绑定门店失败')
      }
    }

    // 新增出资信息
    const createFundDialogVisible = ref(false)
    const fundFormRef = ref(null)
    const createFundForm = reactive({ agreementNo: '', tenantId: '', fundAmount: 0, fundRatio: 0, fundTime: '', fundAccount: '', receiptAccount: '' })
    const fundFormRules = reactive({
      agreementNo: [ { required: true, message: '请选择协议', trigger: 'change' } ],
      tenantId: [ { required: true, message: '请选择出资方', trigger: 'change' } ],
      fundAmount: [ { required: true, message: '请输入出资金额', trigger: 'blur' } ],
      fundRatio: [ { required: true, message: '请输入出资比例', trigger: 'blur' } ],
      fundTime: [ { required: true, message: '请选择出资时间', trigger: 'change' } ],
      fundAccount: [ { required: true, message: '请输入出资账户', trigger: 'blur' } ],
      receiptAccount: [ { required: true, message: '请输入到账账户', trigger: 'blur' } ],
    })
    const openCreateFundDialog = () => {
      createFundForm.agreementNo = selectedAgreementNo.value
      createFundDialogVisible.value = true
      tenantService.getTenantList({ pageNum: 1, pageSize: 1000, keyword: '' }).then(res => {
        tenants.value = res.data || []
      }).catch(() => {})
    }
    const submitCreateFund = async () => {
      try {
        if (fundFormRef.value) {
          await fundFormRef.value.validate()
        }
        const payload = { ...createFundForm }
        payload.fundRatio = Number(payload.fundRatio || 0) / 100
        const res = await assetService.createJointOperationFund(payload)
        if (res.code === 200) {
          ElMessage.success('出资信息新增成功')
          createFundDialogVisible.value = false
          await fetchAllFunderList()
        } else {
          ElMessage.error(res.message || '新增失败')
        }
      } catch (e) {
        ElMessage.error(e.message || '新增失败')
      }
    }

    const onFundAgreementChange = async (agreementNo) => {
      try {
        const res = await tenantService.getTenantList({ pageNum: 1, pageSize: 1000, keyword: '' })
        tenants.value = res.data || []
      } catch (e) {
        // ignore
      }
    }

    // 新增收益分配
    const createProfitDialogVisible = ref(false)
    const profitFormRef = ref(null)
    const createProfitForm = reactive({ agreementNo: '', tenant: '', fundSerialId: '', profitSharingPlan: '', seniorFloorFloorIncome: 0, additionalProfitShareRatio: 0 })
    const openCreateProfitDialog = () => {
      createProfitForm.agreementNo = selectedAgreementNo.value
      createProfitDialogVisible.value = true
      onProfitAgreementChange(createProfitForm.agreementNo)
    }
    const submitCreateProfit = async () => {
      try {
        if (profitFormRef.value) {
          await profitFormRef.value.validate()
        }
        const payload = { ...createProfitForm }
        payload.seniorFloorFloorIncome = Number(payload.seniorFloorFloorIncome || 0) / 100
        payload.additionalProfitShareRatio = Number(payload.additionalProfitShareRatio || 0) / 100
        const res = await assetService.createJointOperationProfitDistribution(payload)
        if (res.code === 200) {
          ElMessage.success('收益分配新增成功')
          createProfitDialogVisible.value = false
          await fetchAllProfitList()
        } else {
          ElMessage.error(res.message || '新增失败')
        }
      } catch (e) {
        ElMessage.error(e.message || '新增失败')
      }
    }

    const onProfitAgreementChange = async (agreementNo) => {
      try {
        if (!agreementNo) return
        createProfitForm.tenant = ''
        createProfitForm.fundSerialId = ''
        createProfitForm.profitSharingPlan = ''

        const fundRes = await assetService.getJointOperationFunds({ agreementNo })
        const funds = fundRes.data || []
        currentAgreementFunds.value = funds
        const tenantMap = {}
        profitTenantOptions.value = funds
          .filter(f => f && f.tenantId != null)
          .map(f => {
            const label = f.tenantName || String(f.tenantId)
            const value = String(f.tenantId)
            if (!tenantMap[value]) {
              tenantMap[value] = true
              return { label, value }
            }
            return null
          })
          .filter(Boolean)
        profitFundOptions.value = []

        const profitRes = await assetService.getProfitByAgreementNo({ agreementNo })
        const allProfit = profitRes.data || []
        if (allProfit.length === 0) {
          allowedProfitPlans.value = ['平层', '劣后层']
        } else {
          const hasPingCeng = allProfit.some(p => p.profitSharingPlan === '平层')
          const hasLieHouCeng = allProfit.some(p => p.profitSharingPlan === '劣后层')
          if (hasPingCeng) {
            allowedProfitPlans.value = ['平层']
          } else if (hasLieHouCeng) {
            allowedProfitPlans.value = ['优先层', '劣后层']
          } else {
            allowedProfitPlans.value = []
          }
        }
      } catch (e) {
        ElMessage.error('加载协议相关信息失败：' + (e.message || ''))
      }
    }

    const onProfitTenantChange = (tenantId) => {
      if (!tenantId) {
        profitFundOptions.value = []
        createProfitForm.fundSerialId = ''
        return
      }
      const matched = (currentAgreementFunds.value || []).filter(f => String(f.tenantId) === String(tenantId))
      profitFundOptions.value = matched
        .filter(f => f && f.id != null)
        .map(f => ({ label: String(f.id), value: String(f.id) }))
      createProfitForm.fundSerialId = matched.length > 0 ? String(matched[0].id) : ''
    }

    const renderTurnoverChart = (list) => {
      nextTick(() => {
        const el = turnoverChart.value
        if (!el) return
        const chart = echarts.init(el)
        const x = list.map(i => i.date)
        const y = list.map(i => Number(i.turnover))
        chart.setOption({
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: x },
          yAxis: { type: 'value' },
          series: [{ name: '营业额', type: 'line', smooth: true, data: y }]
        })
      })
    }

    const fetchTurnoverTrend = async () => {
      try {
        chartLoading.value = true
        const end = new Date()
        const start = new Date()
        start.setFullYear(end.getFullYear() - 1)
        const fmt = (d) => d.toISOString().slice(0, 10)
        const params = {
          brandCode: brandCode,
          storeEntCode: selectedStore.value,
          start_date: fmt(start),
          end_date: fmt(end),
          granularity: timeGranularity.value
        }
        const res = await assetService.getBrandTurnoverTrend(params)
        const list = res.data || []
        renderTurnoverChart(list)
      } catch (e) {
        ElMessage.error('获取营业额趋势失败：' + (e.message || ''))
      } finally {
        chartLoading.value = false
      }
    }

    // 打开新增门店弹窗
    const openAddStoreDialog = () => {
      // 重置表单
      resetStoreForm()
      storeDialogTitle.value = '新增门店'
      activeTab.value = 'manual'
      storeDialogVisible.value = true
    }

    // 编辑门店
    const handleEditStore = (row) => {
      // 填充表单数据，驼峰命名匹配
      storeFormData.storeEntCode = row.storeEntCode
      storeFormData.storeName = row.storeName
      storeFormData.storePattern = row.storePattern
      storeFormData.storeEntName = row.storeEntName
      storeFormData.province = row.province
      storeFormData.city = row.city
      storeFormData.storeBusinessLicense = row.storeBusinessLicense
      storeFormData.groundLeaseTermBegin = row.groundLeaseTermBegin
      storeFormData.groundLeaseTermEnd = row.groundLeaseTermEnd
      storeFormData.rentTerm = row.rentTerm
      storeFormData.entDetailAddress = row.entDetailAddress
      storeFormData.brandCode = brandCode
      storeFormData.investAmountForOpening = row.investAmountForOpening

      storeDialogTitle.value = '编辑门店'
      activeTab.value = 'manual'
      storeDialogVisible.value = true

      // 重置表单验证状态
      nextTick(() => {
        if (storeFormRef.value) {
          storeFormRef.value.clearValidate()
        }
      })
    }

    // 关闭门店弹窗
    const closeStoreDialog = () => {
      storeDialogVisible.value = false
      // 重置表单
      nextTick(() => {
        resetStoreForm()
      })
    }

    // 重置门店表单
    const resetStoreForm = () => {
      storeFormData.storeEntCode = ''
      storeFormData.storeName = ''
      storeFormData.storePattern = ''
      storeFormData.storeEntName = ''
      storeFormData.province = ''
      storeFormData.city = ''
      storeFormData.storeBusinessLicense = ''
      storeFormData.groundLeaseTermBegin = ''
      storeFormData.groundLeaseTermEnd = ''
      storeFormData.rentTerm = null
      storeFormData.entDetailAddress = ''
      storeFormData.brandCode = brandCode
      storeFormData.investAmountForOpening = null

      // 重置批量上传相关
      uploadedFileName.value = ''
      uploadedFile.value = null
      previewStoreList.value = []
      hasInvalidData.value = false

      // 重置表单验证状态
      if (storeFormRef.value) {
        storeFormRef.value.clearValidate()
      }
    }

    // 提交门店表单
    const submitStoreForm = async () => {
      try {
        submittingStore.value = true

        console.error(storeFormData)

        if (activeTab.value === 'manual') {
          // 手动添加
          if (!storeFormRef.value) return
          await storeFormRef.value.validate()

          let response
          if (storeFormData.storeEntCode) {
            // 更新门店
            response = await assetService.updateStore(storeFormData)
          } else {
            // 新增门店
            response = await assetService.createStore(storeFormData)
          }

          if (response.code === 200) {
            ElMessage.success(storeFormData.storeEntCode ? '门店信息更新成功' : '门店创建成功')
            closeStoreDialog()
            getStoreList() // 刷新门店列表
          } else {
            ElMessage.error(response.message || (storeFormData.storeEntCode ? '更新门店信息失败' : '创建门店失败'))
          }
        } else {
          // 批量导入
          if (previewStoreList.value.length === 0) {
            ElMessage.warning('请上传并预览数据')
            return
          }

          if (hasInvalidData.value) {
            ElMessage.warning('存在无效数据，请修正后再提交')
            return
          }

          // 过滤掉校验不通过的数据
          const validStores = previewStoreList.value
              .filter(store => !store.errorMsg)
              .map(store => {
                return {
                  ...store,
                  brandCode: brandCode
                }
              })

          const response = await assetService.batchCreateStores(validStores)
          if (response.code === 200) {
            ElMessage.success(`成功导入 ${response.data} 家门店`)
            closeStoreDialog()
            getStoreList() // 刷新门店列表
          } else {
            ElMessage.error(response.message || '批量导入门店失败')
          }
        }
      } catch (error) {
        if (error.name === 'Error') {
          ElMessage.error(error.message || '操作失败，请重试')
        }
        // 表单验证失败不提示
      } finally {
        submittingStore.value = false
      }
    }

    // 下载批量导入模板
    const downloadTemplate = () => {
      // 创建模板数据，驼峰命名匹配
      const templateData = [
        {
          门店简称: '示例门店',
          门店企业名称: '示例门店有限公司',
          开店模式: '直营',
          所在省份: '北京市',
          所在城市: '北京市',
          门店统一社会信用代码: '91110101MA00000000',
          场地租期开始时间: '2023-01-01',
          场地租期结束时间: '2028-01-01',
          详细地址: '北京市朝阳区XX路XX号',
          开店投资金额: 500000.00
        }
      ]

      // 创建工作簿和工作表
      const ws = XLSX.utils.json_to_sheet(templateData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '门店信息')

      // 生成并下载文件
      XLSX.writeFile(wb, `品牌${brandDetail.brandName || ''}门店导入模板.xlsx`)
    }

    // 处理文件上传
    const handleFileChange = (file) => {
      uploadedFile.value = file.raw
      uploadedFileName.value = file.name

      // 解析Excel文件
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })

          // 获取第一个工作表
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]

          // 转换为JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet)

          // 处理解析结果
          processImportData(jsonData)
        } catch (error) {
          ElMessage.error('文件解析失败，请检查文件格式是否正确')
          console.error('Excel解析错误:', error)
        }
      }
      reader.readAsArrayBuffer(uploadedFile.value)
    }

    // 处理导入的数据 - 驼峰命名匹配
    const processImportData = (data) => {
      if (!data || data.length === 0) {
        ElMessage.warning('未解析到数据，请检查文件内容')
        previewStoreList.value = []
        hasInvalidData.value = false
        return
      }

      // 转换数据格式并验证
      const processedData = data.map((item, index) => {
        // 修复日期解析逻辑
        const parseDate = (dateValue) => {
          if (!dateValue) return ''
          // 处理字符串格式日期
          if (typeof dateValue === 'string') {
            // 匹配YYYY-MM-DD或YYYY/MM/DD格式
            const dateRegex = /^\d{4}[-/]\d{2}[-/]\d{2}$/
            if (dateRegex.test(dateValue)) {
              return dateValue.replace(/\//g, '-') // 统一转换为YYYY-MM-DD
            }
          }
          // 处理Excel数字格式日期（XLSX会将日期转为自1900年1月1日以来的天数）
          if (typeof dateValue === 'number') {
            const excelDate = new Date((dateValue - 25569) * 86400000)
            return excelDate.toISOString().split('T')[0]
          }
          // 其他情况尝试直接转换
          const date = new Date(dateValue)
          return date.toString() === 'Invalid Date' ? '' : date.toISOString().split('T')[0]
        }

        const storeData = {
          rowIndex: index + 2, // 表格行号(从2开始)
          storeName: item.门店简称 || '',
          storePattern: item.开店模式 || '',
          storeEntName: item.门店企业名称 || '',
          province: item.所在省份 || '',
          city: item.所在城市 || '',
          storeBusinessLicense: item.门店统一社会信用代码 || '',
          // 使用修复后的日期解析方法
          groundLeaseTermBegin: parseDate(item.场地租期开始时间),
          groundLeaseTermEnd: parseDate(item.场地租期结束时间),
          entDetailAddress: item.详细地址 || '',
          investAmountForOpening: item.开店投资金额 || null,
          errorMsg: null
        }

        // 验证数据
        const errorMsg = validateStoreData(storeData)
        if (errorMsg) {
          storeData.errorMsg = `第${storeData.rowIndex}行: ${errorMsg}`
        }

        return storeData
      })

      previewStoreList.value = processedData
      hasInvalidData.value = processedData.some(item => item.errorMsg)
    }

    // 上传前检查
    const beforeUpload = (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.type === 'application/vnd.ms-excel'
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isExcel) {
        ElMessage.error('只能上传Excel文件!')
        return false
      }
      if (!isLt5M) {
        ElMessage.error('文件大小不能超过5MB!')
        return false
      }

      return true
    }

    // 清除已上传文件
    const clearUploadedFile = () => {
      uploadedFileName.value = ''
      uploadedFile.value = null
      previewStoreList.value = []
      hasInvalidData.value = false
    }

    // 编辑品牌
    const handleEditBrand = () => {
      router.push({
        path: '/brandManagement',
        query: { editBrandCode: brandDetail.brandCode }
      })
    }

    // 返回列表
    const handleBack = () => {
      router.push('/brandManagement')
    }

    // 门店分页大小变化
    const handleStoreSizeChange = (size) => {
      storePagination.pageSize = size
      getStoreList()
    }

    // 门店当前页变化
    const handleStoreCurrentChange = (page) => {
      storePagination.currentPage = page
      getStoreList()
    }

    // 门店选择行变化
    const handleStoreSelectionChange = () => {
      // 可以在这里处理批量操作
    }

    // 页面挂载时加载数据
    onMounted(async () => {
      if (brandCode) {
        await getBrandDetail()
        await getStoreList()
        await getAgreementList()
        await fetchBankMappings()
        await fetchTurnoverTrend()
      } else {
        ElMessage.error('未指定品牌编码')
        loading.value = false
        storeLoading.value = false
      }
    })

    watch([selectedStore, timeGranularity], () => {
      fetchTurnoverTrend()
    })

    const bankMapList = ref([])
    const bankMapLoading = ref(false)
    const addBankMapDialogVisible = ref(false)
    const addBankMapFormRef = ref(null)
    const addBankMapForm = reactive({ payerAccount: '', bankName: '', effectiveFrom: '', effectiveTo: '', status: 1, storeEntCodes: [] })
    const addBankMapRules = reactive({
      payerAccount: [{ required: true, message: '请输入打款账号', trigger: 'blur' }],
      storeEntCodes: [{ required: true, message: '请选择门店', trigger: 'change' }]
    })
    const openAddBankMapDialog = () => {
      addBankMapDialogVisible.value = true
    }
    const submitAddBankMap = async () => {
      try {
        await addBankMapFormRef.value.validate()
        const payloads = (addBankMapForm.storeEntCodes || []).map(code => ({
          storeEntCode: code,
          payerAccount: addBankMapForm.payerAccount,
          bankName: addBankMapForm.bankName,
          effectiveFrom: addBankMapForm.effectiveFrom || null,
          effectiveTo: addBankMapForm.effectiveTo || null,
          status: addBankMapForm.status
        }))
        for (const p of payloads) {
          await assetService.mapAccount(p)
        }
        ElMessage.success('新增映射成功')
        addBankMapDialogVisible.value = false
        await fetchBankMappings()
      } catch (e) {
        ElMessage.error(e.message || '新增映射失败')
      }
    }
    const changeBankMapStatus = async (row, val) => {
      try {
        await assetService.changeAccountStatus({ id: row.id, status: val })
        ElMessage.success('状态已更新')
      } catch (e) {
        ElMessage.error(e.message || '状态更新失败')
      }
    }

    return {
      brandDetail,
      storeList,
      storePagination,
      loading,
      storeLoading,
      agreementLoading,
      submittingStore,
      storeDialogVisible,
      storeDialogTitle,
      activeTab,
      storeFormData,
      storeFormRules,
      storeFormRef,
      uploadedFileName,
      previewStoreList,
      hasInvalidData,
      licenseDialogVisible,
      currentLicenseUrl,
      formatDate,
      hasPermission,
      getBrandDetail,
      getStoreList,
      openAddStoreDialog,
      handleEditStore,
      closeStoreDialog,
      submitStoreForm,
      downloadTemplate,
      handleFileChange,
      beforeUpload,
      clearUploadedFile,
      handleEditBrand,
      handleBack,
      handleStoreSizeChange,
      handleStoreCurrentChange,
      handleStoreSelectionChange,
      chartLoading,
      selectedStore,
      timeGranularity,
      turnoverChart,
      fetchTurnoverTrend,
      renderTurnoverChart,
      handleView,
      // 协议管理
      agreementList,
      agreementPagination,
      selectedAgreementNo,
      handleAgreementSelectionChange,
      handleAgreementSizeChange,
      handleAgreementCurrentChange,
      // 出资与收益分配
      funderList,
      profitList,
      tenants,
      profitTenantOptions,
      profitFundOptions,
      allowedProfitPlans,
      // 新增品牌级协议
      openCreateAgreementDialog,
      createAgreementDialogVisible,
      createAgreementForm,
      createAgreementFormRef,
      createAgreementRules,
      onCreateStoreCheckChange,
      submitCreateAgreement,
      // 绑定门店
      bindStoresDialogVisible,
      bindStoresSelection,
      bindStoresData,
      onBindStoreCheckChange,
      openBindStoresDialog,
      submitBindStores,
      // 新增出资
      openCreateFundDialog,
      createFundDialogVisible,
      createFundForm,
      submitCreateFund,
      onFundAgreementChange,
      // 新增收益分配
      openCreateProfitDialog,
      createProfitDialogVisible,
      createProfitForm,
      submitCreateProfit,
      onProfitAgreementChange,
      onProfitTenantChange,
      bankMapList,
      bankMapLoading,
      openAddBankMapDialog,
      addBankMapDialogVisible,
      addBankMapForm,
      addBankMapFormRef,
      addBankMapRules,
      submitAddBankMap,
      changeBankMapStatus,
      // 营业额上报
      turnoverReportDialogVisible,
      submittingTurnover,
      turnoverReportForm,
      turnoverReportRules,
      turnoverReportFormRef,
      openTurnoverReportDialog,
      submitTurnoverReport
    }
  }
}
</script>

<style scoped>
.brand-detail-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.breadcrumb {
  margin-bottom: 20px;
}

.main-card {
  background-color: #fff;
  border-radius: 4px;
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #eee;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

/* 按钮文字居中样式 */
.btn-center {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* 品牌信息区域 */
.brand-info-section,
.store-list-section {
  padding: 0 20px 20px;
  margin-bottom: 15px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 5px;
  border-bottom: 1px solid #eee;
}

/* 门店列表区域 */
.store-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.pagination-container {
  margin-top: 15px;
  text-align: right;
  padding-right: 10px;
}

/* 门店表单样式 */
.store-form {
  margin-top: 15px;
}

.turnover-chart-section {
  padding: 0 20px 20px;
  margin-bottom: 15px;
}
.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.filter-controls {
  display: inline-flex;
  gap: 10px;
}
.chart-container {
  width: 100%;
  height: 360px;
}

/* 批量导入区域 */
.batch-import-section {
  padding: 10px 0;
}

.import-guide {
  line-height: 1.8;
  margin-bottom: 20px;
}

.import-guide p {
  margin: 10px 0;
}

.uploaded-file-info {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 4px;
  color: #67c23a;
  margin-top: 10px;
}

.remove-file-btn {
  color: #f56c6c;
  margin-left: auto;
  padding: 0 5px;
}

.preview-section {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px dashed #ccc;
}

.preview-section h4 {
  margin: 0 0 10px;
  font-size: 14px;
  color: #333;
}

.error-text {
  color: #f56c6c;
  font-size: 12px;
}

/* 门店名称链接样式 */
.store-name-link {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}

.success-text {
  color: #67c23a;
  font-size: 12px;
}

.validation-warning {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #fef0f0;
  border: 1px solid #fee2e2;
  border-radius: 4px;
  color: #f56c6c;
  font-size: 13px;
  display: flex;
  align-items: center;
}

.validation-warning i {
  margin-right: 5px;
}

/* 营业执照查看弹窗 */
.license-container {
  text-align: center;
  padding: 10px;
}

.license-image {
  max-width: 100%;
  max-height: 500px;
  border: 1px solid #eee;
  border-radius: 4px;
}

.no-license {
  padding: 50px 0;
  color: #999;
}

/* 描述列表样式优化 */
::v-deep .el-descriptions__label {
  font-weight: 500;
  background-color: #fafafa;
}

::v-deep .el-descriptions__cell {
  padding: 12px 16px;
}

/* 表格样式优化 */
::v-deep .el-table th {
  background-color: #f5f7fa;
  font-weight: 600;
}

::v-deep .el-table tr:hover > td {
  background-color: #f9fafb !important;
}

/* 响应式调整 */
@media (max-width: 992px) {
  .el-col {
    width: 100% !important;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
  }

  .header-actions .el-button {
    margin-bottom: 10px;
  }
}
</style>


